import { type MetaDescriptor } from 'react-router'

const SITE_NAME = 'Huskonsertfestivalen'
const SITE_URL = 'https://huskonsertfestivalen.no'
export const SITE_DESCRIPTION =
	'Huskonsertar i private heimar og naust på Vorrå, Karmøy — ein festival for heile familien, arrangert av lokale musikantar.'

/** A picture for link previews; a path is made absolute against the site. */
type ShareImage = {
	url: string
	width: number
	height: number
	alt: string
	type?: 'image/png' | 'image/jpeg'
}

/** The festival's own share card (`pnpm og:image`), for every other page. */
export const FESTIVAL_SHARE_IMAGE: ShareImage = {
	url: '/og/huskonsertfestivalen.png',
	width: 1200,
	height: 630,
	alt: 'Eit hus i natta med døra open, lys og notar som strøymer ut — Huskonsertfestivalen på Vorrå',
	type: 'image/png',
}

/**
 * A page's title and description, plus the Open Graph tags that link
 * previews (Facebook, Messenger, WhatsApp, Slack, iMessage …) are built from:
 * title, description, the canonical URL and a 1200 × 630 picture. X reads the
 * same `og:` tags; it only needs to be told to show the picture large.
 *
 * A route's meta replaces the root's, so every route with its own meta calls
 * this; routes without one inherit the root's.
 */
export function pageMeta(
	{ location, matches }: MetaContext,
	page: {
		/** "Artistane" → "Artistane | Huskonsertfestivalen"; none on the front page */
		title?: string | null
		/** the preview's headline, when it should differ from the title */
		shareTitle?: string
		description?: string | null
		image?: ShareImage
	} = {},
): MetaDescriptor[] {
	const origin = requestOrigin(matches) ?? SITE_URL
	const title = page.title ? `${page.title} | ${SITE_NAME}` : SITE_NAME
	const shareTitle = page.shareTitle ?? page.title ?? SITE_NAME
	const description = page.description?.trim() || SITE_DESCRIPTION
	const image = page.image ?? FESTIVAL_SHARE_IMAGE
	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:url', content: new URL(location.pathname, origin).href },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:site_name', content: SITE_NAME },
		{ property: 'og:locale', content: 'nn_NO' },
		{ property: 'og:title', content: shareTitle },
		{ property: 'og:description', content: description },
		{ property: 'og:image', content: new URL(image.url, origin).href },
		...(image.type ? [{ property: 'og:image:type', content: image.type }] : []),
		{ property: 'og:image:width', content: String(image.width) },
		{ property: 'og:image:height', content: String(image.height) },
		{ property: 'og:image:alt', content: image.alt },
		{ name: 'twitter:card', content: 'summary_large_image' },
	]
}

/** The part of a meta function's arguments this needs (any route's fits). */
type MetaContext = {
	location: { pathname: string }
	matches: ReadonlyArray<{ id: string; loaderData?: unknown } | undefined>
}

/** The origin the root loader saw ("https://huskonsertfestivalen.no"). */
function requestOrigin(matches: MetaContext['matches']) {
	const root = matches.find((match) => match?.id === 'root')?.loaderData as
		| { requestInfo?: { origin?: string } }
		| undefined
	return root?.requestInfo?.origin
}
